import * as React from 'react'
import { useMemo } from 'react'
import { getMDXComponent } from 'mdx-bundler/client'
import {
	Callout,
	ComparisonCard,
	CTA,
	DataTable,
	Disclaimer,
	MetricBars,
	ProcessFlow,
	Quote,
	StatRow,
} from '#app/components/blog/visual-components.tsx'
import { type BlogPost, type BlogPostListItem } from './mdx.server.ts'

/**
 * Custom components for MDX rendering
 */
const mdxComponents = {
	// Blog visual components
	StatRow,
	Callout,
	ProcessFlow,
	ComparisonCard,
	Quote,
	MetricBars,
	DataTable,
	CTA,
	Disclaimer,
	// Override default elements with custom styling
	h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
		<h1
			className="mb-6 mt-12 text-3xl font-bold tracking-tight text-stone-900 first:mt-0"
			{...props}
		/>
	),
	h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
		<h2
			className="mb-4 mt-10 text-2xl font-semibold tracking-tight text-stone-900"
			{...props}
		/>
	),
	h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => (
		<h3
			className="mb-3 mt-8 text-xl font-semibold text-stone-900"
			{...props}
		/>
	),
	p: (props: React.HTMLAttributes<HTMLParagraphElement>) => (
		<p className="mb-6 leading-relaxed text-stone-700" {...props} />
	),
	a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
		<a
			className="font-medium text-purple-700 underline decoration-purple-300 underline-offset-2 transition-colors hover:text-purple-900 hover:decoration-purple-500"
			{...props}
		/>
	),
	ul: (props: React.HTMLAttributes<HTMLUListElement>) => (
		<ul className="mb-6 ml-6 list-disc space-y-2 text-stone-700" {...props} />
	),
	ol: (props: React.HTMLAttributes<HTMLOListElement>) => (
		<ol
			className="mb-6 ml-6 list-decimal space-y-2 text-stone-700"
			{...props}
		/>
	),
	li: (props: React.HTMLAttributes<HTMLLIElement>) => (
		<li className="leading-relaxed" {...props} />
	),
	blockquote: (props: React.HTMLAttributes<HTMLQuoteElement>) => (
		<blockquote
			className="my-6 border-l-4 border-purple-200 bg-stone-50 py-4 pl-6 pr-4 italic text-stone-600"
			{...props}
		/>
	),
	code: (props: React.HTMLAttributes<HTMLElement>) => {
		// Inline code (not inside pre)
		const isInline = typeof props.children === 'string'
		if (isInline) {
			return (
				<code
					className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-sm text-purple-800"
					{...props}
				/>
			)
		}
		return <code {...props} />
	},
	pre: (props: React.HTMLAttributes<HTMLPreElement>) => (
		<pre
			className="my-6 overflow-x-auto rounded-lg bg-stone-900 p-4 text-sm"
			{...props}
		/>
	),
	hr: () => <hr className="my-10 border-t border-stone-200" />,
	img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
		<img className="my-6 rounded-lg" {...props} />
	),
	table: (props: React.TableHTMLAttributes<HTMLTableElement>) => (
		<div className="my-6 overflow-x-auto">
			<table className="min-w-full divide-y divide-stone-200" {...props} />
		</div>
	),
	th: (props: React.ThHTMLAttributes<HTMLTableCellElement>) => (
		<th
			className="bg-stone-50 px-4 py-3 text-left text-sm font-semibold text-stone-900"
			{...props}
		/>
	),
	td: (props: React.TdHTMLAttributes<HTMLTableCellElement>) => (
		<td className="border-t border-stone-100 px-4 py-3 text-sm" {...props} />
	),
}

/**
 * Hook to get the MDX component from compiled code
 */
export function useMdxComponent(code: string) {
	return useMemo(() => getMDXComponent(code), [code])
}

/**
 * Render compiled MDX content
 */
export function MdxContent({ code }: { code: string }) {
	const Component = useMdxComponent(code)
	return <Component components={mdxComponents} />
}

/**
 * Generate meta tags for a blog post
 */
export function getBlogPostMeta(
	post: BlogPost | BlogPostListItem,
	siteUrl: string,
) {
	const url = `${siteUrl}/blog/${post.slug}`
	const imageUrl = post.frontmatter.image
		? `${siteUrl}${post.frontmatter.image}`
		: `${siteUrl}/img/og-blog.png`

	return [
		{ title: `${post.frontmatter.title} | MyBillingsMethod Blog` },
		{ name: 'description', content: post.frontmatter.description },
		{ name: 'author', content: post.frontmatter.author },
		{
			name: 'keywords',
			content: [
				...post.frontmatter.categories,
				...post.frontmatter.keywords,
			].join(', '),
		},

		// Open Graph
		{ property: 'og:type', content: 'article' },
		{ property: 'og:url', content: url },
		{ property: 'og:title', content: post.frontmatter.title },
		{ property: 'og:description', content: post.frontmatter.description },
		{ property: 'og:image', content: imageUrl },
		{ property: 'og:site_name', content: 'MyBillingsMethod' },
		{
			property: 'article:published_time',
			content: new Date(post.frontmatter.date).toISOString(),
		},
		{ property: 'article:author', content: post.frontmatter.author },
		...post.frontmatter.categories.map((category) => ({
			property: 'article:tag',
			content: category,
		})),

		// Twitter
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:url', content: url },
		{ name: 'twitter:title', content: post.frontmatter.title },
		{ name: 'twitter:description', content: post.frontmatter.description },
		{ name: 'twitter:image', content: imageUrl },
		{
			name: 'twitter:image:alt',
			content: post.frontmatter.imageAlt ?? post.frontmatter.title,
		},
	]
}

/**
 * Generate structured data for a blog post
 */
export function getBlogPostStructuredData(
	post: BlogPost | BlogPostListItem,
	siteUrl: string,
) {
	const url = `${siteUrl}/blog/${post.slug}`
	const imageUrl = post.frontmatter.image
		? `${siteUrl}${post.frontmatter.image}`
		: `${siteUrl}/img/og-blog.png`

	return {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: post.frontmatter.title,
		description: post.frontmatter.description,
		image: imageUrl,
		url,
		datePublished: new Date(post.frontmatter.date).toISOString(),
		dateModified: new Date(post.frontmatter.date).toISOString(),
		author: {
			'@type': 'Organization',
			name: post.frontmatter.author,
			url: siteUrl,
		},
		publisher: {
			'@type': 'Organization',
			name: 'MyBillingsMethod',
			logo: {
				'@type': 'ImageObject',
				url: `${siteUrl}/logo.png`,
			},
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': url,
		},
		wordCount: post.readTime.words,
		keywords: [...post.frontmatter.categories, ...post.frontmatter.keywords],
	}
}

/**
 * Generate blog list page structured data
 */
export function getBlogListStructuredData(
	posts: BlogPostListItem[],
	siteUrl: string,
) {
	return {
		'@context': 'https://schema.org',
		'@type': 'Blog',
		name: 'MyBillingsMethod Blog',
		description:
			'Articles about natural family planning, the Billings Method, fertility awareness, and privacy-first health tracking.',
		url: `${siteUrl}/blog`,
		publisher: {
			'@type': 'Organization',
			name: 'MyBillingsMethod',
			logo: {
				'@type': 'ImageObject',
				url: `${siteUrl}/logo.png`,
			},
		},
		blogPost: posts.slice(0, 10).map((post) => ({
			'@type': 'BlogPosting',
			headline: post.frontmatter.title,
			description: post.frontmatter.description,
			url: `${siteUrl}/blog/${post.slug}`,
			datePublished: new Date(post.frontmatter.date).toISOString(),
		})),
	}
}
